import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, X, RefreshCw, Save, FolderOpen, Copy, Star as StarIcon, Trash2, Sparkles, Calendar, BarChart3, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import {
  getSquadPillarScores, getFormations,
  getLineups, getDefaultLineup, getLineup, createLineup, updateLineup, setDefaultLineup,
  getMatchesForLineup, getOfficialLineup, saveOfficialLineup, getOfficialLineupStats,
} from '../api';
import { useTheme } from '../ThemeContext';
import PageChrome from '../components/PageChrome';
import StarRating from '../components/StarRating';
import LineupSlotPicker from '../components/LineupSlotPicker';
import PlayerDetailPanel from '../components/PlayerDetailPanel';

// Stage 7b: fully interactive squad builder on top of Stage 7a's schema — no persistence
// yet (that's Stage 7e/7f). Everything below (slots/bench/formation) is in-memory React
// state only; nothing is written to lineups/lineup_slots from this page.
//
// Visual layout reskinned 2026-07-23 per design_handoff_squad_builder/ (tier-colored cards,
// crest/name/rating header, tier legend) — the handoff's own "no interactivity, just the
// static look" scope note describes what THAT mockup covers, not an instruction to strip the
// picker/formation-switch/detail-panel logic already built and verified here; this reskins
// the existing interactive page rather than replacing it with a second static one.

const PERIODS = [
  { value: 'week', label: 'Week' },
  { value: '28day', label: '28-Day' },
  { value: 'preseason', label: 'Pre-season' },
  { value: 'season', label: 'Season' },
];

// Pitch trapezoid geometry (2026-07-23 redesign — real photographic-perspective pitch image
// instead of a flat CSS rectangle). Measured, not eyeballed: scripts/measure_pitch.js reads
// public/pitch-3d.png's raw alpha channel row-by-row to find the trapezoid's left/right edges
// at every row, then fits halfWidth(u) and centerY(u) as linear functions of depth u (0 = far
// touchline / image top / narrow end, 1 = near touchline / image bottom / wide end — matching
// the GK-at-bottom, ST-at-top orientation already fixed for this page). Values are % of the
// image's own box, so a slot's on-pitch position stays correct at any container size as long
// as the pitch panel keeps the image's native aspect ratio (no JS-computed transform:scale
// needed the way the old fixed-canvas approach required).
const PITCH_IMAGE_ASPECT = 850 / 540;
const PITCH_HALF_WIDTH_U0 = 19.637; // % of width at u=0 (far touchline)
const PITCH_HALF_WIDTH_U1 = 46.614; // % of width at u=1 (near touchline)
const PITCH_CENTER_Y_U0 = 4.074;    // % of height at u=0 (far touchline row)
const PITCH_CENTER_Y_U1 = 94.074;   // % of height at u=1 (near touchline row)
const pitchHalfWidth = u => PITCH_HALF_WIDTH_U0 + u * (PITCH_HALF_WIDTH_U1 - PITCH_HALF_WIDTH_U0);
const pitchCenterY = u => PITCH_CENTER_Y_U0 + u * (PITCH_CENTER_Y_U1 - PITCH_CENTER_Y_U0);
// lateral: 0 (left touchline) .. 1 (right touchline). u: 0 (far touchline) .. 1 (near touchline).
// Existing formation slot data stores x/y as 0-100 on the old flat canvas — reused unchanged
// here as lateral = x/100, u = y/100, just run through the trapezoid-corrected formula instead
// of applied as a straight percentage, so no formation/schema data needed to change.
const pitchSlotX = (lateral, u) => 50 + (2 * lateral - 1) * pitchHalfWidth(u);
const pitchSlotY = u => pitchCenterY(u);
// Same 4 corners the geometry above was fit from — clips the image to the trapezoid so the
// transparent margin around it (and any background bleed) never shows; the dark surface
// behind it is this page's own CSS, not baked into the image.
const PITCH_CLIP_PATH = 'polygon(30.33% 4.07%, 69.60% 4.07%, 96.57% 94.07%, 3.34% 94.07%)';

const PILLAR_KEYS = ['physical', 'technical', 'tactical', 'identity', 'availability'];
const PILLAR_LABELS = { physical: 'Physical', technical: 'Technical', tactical: 'Tactical', identity: 'Identity', availability: 'Availability' };

// Einstein's ranking criterion (2026-07-24) — overall plus each of the 5 pillars individually,
// so "Einstein Physical" fills every open slot with the highest-physical eligible player
// instead of highest-overall. Same key set LineupSlotPicker's own stat toggle uses below.
const EINSTEIN_CRITERIA = [{ key: 'overall', label: 'Overall' }, ...PILLAR_KEYS.map(k => ({ key: k, label: PILLAR_LABELS[k] }))];

// Rating tiers — same 4 bands and colors in both themes (design handoff: "card colors are
// the same in both themes — only the page chrome changes"). Bronze/Silver/Gold's gradients
// are flat; Elite's is a multi-stop shimmer plus an outer glow, the deliberately rarest/
// brightest tier. A player with no data for the selected period isn't tiered at all — a
// distinct neutral state (see NO_DATA_STYLE), same "no data ≠ 0" rule as everywhere else.
const TIERS = [
  { key: 'elite',  min: 80, label: 'Elite (80+)',    bg: 'linear-gradient(155deg,#fff3c4 0%,#ffd769 28%,#f0b93d 55%,#ffe9a3 78%,#d6a020 100%)', border: '#fff0b0', text: '#4a3200', glow: '0 0 20px rgba(255,215,110,0.5)' },
  { key: 'gold',   min: 70, label: 'Gold (70-79)',   bg: 'linear-gradient(160deg,#f7dd58,#d4af13)', border: '#ffec8a', text: '#4a3a00', glow: null },
  { key: 'silver', min: 60, label: 'Silver (60-69)', bg: 'linear-gradient(160deg,#e6eaed,#9aa3ab)', border: '#f4f7f9', text: '#20242a', glow: null },
  { key: 'bronze', min: 0,  label: 'Bronze (<60)',   bg: 'linear-gradient(160deg,#c98f56,#7a4d29)', border: '#e0aa71', text: '#2b1a0d', glow: null },
];
function tierOf(overall) {
  if (overall == null) return null;
  return TIERS.find(t => overall >= t.min);
}

// formations.slots' slot_position values are the app's base position code (GK/CB/FB/CDM/
// CAM/ST/W — Stage 2a's investigated convention) plus an optional _L/_R/_1/_2 suffix where a
// formation needs more than one of that position (Stage 7a's uniqueness fix). Stripping the
// suffix recovers the base code for eligibility filtering.
function basePositionOf(slotPosition) {
  return slotPosition ? slotPosition.split('_')[0] : null;
}

function normalizeSquadRow(r) {
  const n = v => (v === null || v === undefined ? null : Number(v));
  return {
    ...r,
    physical: n(r.physical), technical: n(r.technical), tactical: n(r.tactical),
    identity: n(r.identity), availability: n(r.availability), overall: n(r.overall),
    stars: n(r.stars),
  };
}

// Card dimensions/type scale (2026-07-23) — shrunk from the design handoff's literal 140x188/
// 100x136 and the 5-KPI stat row dropped entirely, both to fix real overlap against the
// pitch's existing slot spacing (left untouched per explicit instruction) and to give the
// photo more room. No explicit card height — it sizes to its own content now, so nothing
// inside it is ever forced to compress or get clipped by a guessed-too-short fixed height.
const CARD_DIMS = {
  starter: { w: 96, pad: '7px 6px 6px', r: 13, photoW: 64, photoH: 66, photoR: 8, ratingFs: 17, posFs: 9, nameFs: 10.5, badgeTop: 7, badgeLeft: 7, photoPadTop: 18, dividerH: 1.2, dividerMargin: 10, nameMt: 4, fallbackFs: 20 },
  bench:   { w: 70, pad: '5px 4px 4px', r: 10, photoW: 46, photoH: 48, photoR: 6, ratingFs: 13, posFs: 7, nameFs: 8, badgeTop: 5, badgeLeft: 5, photoPadTop: 12, dividerH: 1, dividerMargin: 6, nameMt: 3, fallbackFs: 15 },
};

// Shared by pitch slots and the bench strip. Main card body opens the detail panel (Stage
// 5b's Individual report, reused via PlayerDetailPanel); the two small overlay icons handle
// the picker actions the "clicking any slot opens a picker" requirement covers for an
// ALREADY-FILLED slot — reassign (swap to a different player) and remove (clear back to
// empty) — since the main click surface is claimed by the detail-panel requirement once a
// card is populated. Empty slots (no card yet) go straight to the picker on click, no
// ambiguity there.
//
// Drag-and-drop (2026-07-23): every placed card is both a drag source and a drop target —
// dragging one onto another slot/bench card swaps the two; dropping onto empty space (bench's
// "+Add") moves it there. The picker remains how you place a brand-new player into an empty
// slot; drag only rearranges players already on the sheet. `isDragOver` just drives a border
// highlight while something's being dragged over this card — native HTML5 DnD, no library.
function PlayerCard({ row, compact, onView, onReassign, onRemove, onDragStart, onDragOver, onDrop, onDragEnter, onDragLeave, isDragOver }) {
  const d = compact ? CARD_DIMS.bench : CARD_DIMS.starter;
  const tier = tierOf(row.overall);
  const cardStyle = tier
    ? { background: tier.bg, border: `1.5px solid ${tier.border}`, boxShadow: `0 8px 20px rgba(0,0,0,0.35)${tier.glow ? `, ${tier.glow}` : ''}` }
    : { background: 'rgba(148,163,184,0.25)', border: '1.5px solid rgba(148,163,184,0.4)', boxShadow: '0 8px 20px rgba(0,0,0,0.3)' };
  const textColor = tier ? tier.text : 'rgba(255,255,255,0.75)';

  return (
    <div style={{ position: 'relative', width: d.w, flexShrink: 0 }}
      draggable onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
      onDragEnter={onDragEnter} onDragLeave={onDragLeave}
    >
      <button type="button" onClick={onView} title={`View ${row.name} — drag to swap`}
        style={{
          position: 'relative', width: d.w, padding: d.pad, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column', borderRadius: d.r, cursor: 'grab', ...cardStyle,
          outline: isDragOver ? '3px solid #22d3ee' : 'none', outlineOffset: 2,
        }}>
        {/* Rating badge — top-left, above/in front of the photo (absolute + out of the flex
            flow, so the photo below never overlaps it). */}
        <div style={{ position: 'absolute', left: d.badgeLeft, top: d.badgeTop, textAlign: 'left' }}>
          <div style={{ fontSize: d.ratingFs, fontWeight: 800, lineHeight: 1, color: textColor }}>
            {row.overall != null ? Math.round(row.overall) : '—'}
          </div>
          <div style={{ fontSize: d.posFs, fontWeight: 700, letterSpacing: '0.5px', color: textColor, marginTop: 2 }}>
            {row.position || ''}
          </div>
        </div>

        {/* Photo — cropped to head-and-shoulders, not the full upper body (2026-07-23). cover
            (not contain) so the photo fills the frame at full size rather than shrinking to
            fit; objectPosition 'top' crops from the BOTTOM of the source photo (torso/chest)
            while keeping the top (head) intact, since player photos have the head near the
            top and the body extending down — cropping from the top would cut the head off
            instead. Rounded top corners only, anchored to the bottom of the space below the
            badge. */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingTop: d.photoPadTop }}>
          <div style={{ width: d.photoW, height: d.photoH, borderRadius: `${d.photoR}px ${d.photoR}px 0 0`, overflow: 'hidden', flexShrink: 0, boxShadow: '0 6px 14px rgba(0,0,0,0.25)' }}>
            {row.photo_url ? (
              <img src={row.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: d.fallbackFs, fontWeight: 800, color: textColor }}>
                {(row.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>

        <div style={{ height: d.dividerH, flexShrink: 0, margin: `0 ${d.dividerMargin}px`, background: 'rgba(255,255,255,0.6)', mixBlendMode: 'overlay', borderRadius: 2 }} />

        <div style={{ flexShrink: 0, fontSize: d.nameFs, fontWeight: 800, color: textColor, textAlign: 'center', marginTop: d.nameMt, padding: '0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {(row.name || '').split(' ').slice(-1)[0]}
        </div>
      </button>
      <button type="button" onClick={onRemove} title="Remove"
        style={{ position: 'absolute', top: -6, left: -6, width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #ef4444', background: '#fff', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>
        <X size={11} />
      </button>
      <button type="button" onClick={onReassign} title="Change player"
        style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #0891b2', background: '#fff', color: '#0891b2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>
        <RefreshCw size={10} />
      </button>
    </div>
  );
}

// Not part of the design handoff's scope (card component only) — resized here purely to keep
// an empty slot roughly the same footprint as its filled counterpart, so formations don't
// visually jump in size as slots fill in. Pitch/bench positions themselves are untouched.
// PlayerCard no longer has a fixed height (see CARD_DIMS), so this keeps its own explicit
// height as a reasonable approximation of PlayerCard's natural rendered size.
function EmptySlotCard({ label, isDay, onClick, compact, onDragOver, onDrop, onDragEnter, onDragLeave, isDragOver }) {
  const d = compact ? CARD_DIMS.bench : CARD_DIMS.starter;
  const h = compact ? 92 : 122;
  return (
    <button type="button" onClick={onClick} title={`Add ${label}`}
      onDragOver={onDragOver} onDrop={onDrop} onDragEnter={onDragEnter} onDragLeave={onDragLeave}
      style={{
        width: d.w, height: h, flexShrink: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '12px 6px', borderRadius: d.r, cursor: 'pointer',
        // Light grey @ 25% opacity ("75% transparent", 2026-07-23) — against the real pitch
        // photo behind it, the old near-invisible rgba(0,0,0,0.03)/rgba(255,255,255,0.04) fill
        // basically disappeared into the grass texture. Not day/night-conditional like the
        // rest of the page chrome — this sits on the photo, not the page background, so it
        // needs to read the same regardless of theme.
        background: isDragOver ? 'rgba(34,211,238,0.35)' : 'rgba(211,211,211,0.25)',
        border: `1.5px dashed ${isDragOver ? '#22d3ee' : 'rgba(255,255,255,0.6)'}`,
      }}>
      <Plus size={compact ? 16 : 20} color="rgba(255,255,255,0.85)" />
      <div style={{ fontSize: compact ? 10 : 11, fontWeight: 700, letterSpacing: '0.5px', color: 'rgba(255,255,255,0.85)' }}>{label}</div>
    </button>
  );
}

// Horizontal percentage bar for one of the 5 pillars, data label at the end of the bar
// (2026-07-23) — scoped to the currently selected squad (this lineup's starters), same
// "no data ≠ 0" rule: a pillar nobody currently on the pitch has data for renders an empty
// track and "no data" instead of a 0%-filled bar.
function KpiBar({ label, value, isDay, accent }) {
  const hasData = value != null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 84, fontSize: 12, fontWeight: 700, color: isDay ? 'rgba(20,24,28,0.6)' : 'rgba(255,255,255,0.6)', flexShrink: 0 }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 8, borderRadius: 999, background: isDay ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
        {hasData && (
          <div style={{ width: `${Math.max(0, Math.min(100, value))}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg,#22d3ee,#0891b2)' }} />
        )}
      </div>
      <div style={{ width: 52, textAlign: 'right', fontSize: 12, fontWeight: 700, flexShrink: 0, color: hasData ? accent : (isDay ? 'rgba(20,24,28,0.35)' : 'rgba(255,255,255,0.35)') }}>
        {hasData ? `${Math.round(value)}%` : 'no data'}
      </div>
    </div>
  );
}

// Pill button matching PageChrome's nav-pill aesthetic — used for Save/Save as new/Load.
function ActionButton({ onClick, icon: Icon, children, isDay, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 999,
        border: `1px solid ${isDay ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)'}`,
        background: isDay ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.06)',
        color: isDay ? '#1c2024' : '#fff', cursor: disabled ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700,
        opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
      }}>
      <Icon size={14} color={isDay ? '#0891b2' : '#22d3ee'} /> {children}
    </button>
  );
}

// Full-width variant of ActionButton for the sidebar column (2026-07-23, "Clear squad" /
// "Einstein") — same pill styling, just block-level to match the sidebar's own 260px width
// instead of hugging its label. tone="danger" reddens Clear squad's icon/border since it wipes
// the whole working lineup, same visual weight TrainerRatings gives its own delete button.
function SidebarActionButton({ onClick, icon: Icon, children, isDay, tone }) {
  const danger = tone === 'danger';
  const iconColor = danger ? '#ef4444' : (isDay ? '#0891b2' : '#22d3ee');
  return (
    <button type="button" onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '9px 14px', borderRadius: 10, width: '100%', boxSizing: 'border-box',
        border: `1px solid ${danger ? 'rgba(239,68,68,0.4)' : (isDay ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)')}`,
        background: isDay ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.06)',
        color: danger ? '#ef4444' : (isDay ? '#1c2024' : '#fff'), cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
      }}>
      <Icon size={14} color={iconColor} /> {children}
    </button>
  );
}

// 'YYYY-MM-DD' -> 'DD-MM-YYYY' without going through Date parsing — new Date('YYYY-MM-DD')
// parses as UTC midnight, and .toLocaleDateString() then renders in the browser's local
// timezone, which can shift the displayed day by one for anyone west of UTC. A plain string
// split sidesteps that entirely for what's already a pure calendar date, no time component.
function formatMatchDate(dateStr) {
  const [y, m, d] = dateStr.split('-');
  return `${d}-${m}-${y}`;
}

// One row in the Official Line-up modal's match picker (2026-08-07) — played and upcoming
// matches share this same row, distinguished only by the "Played"/"Scheduled" caption and
// whether officialLineupId is set ("Saved" badge, this match already has an official lineup).
function MatchRow({ match, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', border: 'none', borderTop: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-row-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {match.logoUrl ? (
        <img src={match.logoUrl} alt="" style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }} />
      ) : (
        <div style={{ width: 24, height: 24, flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          vs {match.opponentName}{match.homeAway ? ` (${match.homeAway})` : ''}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {formatMatchDate(match.date)} · {match.played ? 'Played' : 'Scheduled'}
        </div>
      </div>
      {match.officialLineupId && (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'rgba(34,211,238,0.15)', color: '#0891b2', flexShrink: 0 }}>
          Saved
        </span>
      )}
    </button>
  );
}

// Same NULL-to-bottom-regardless-of-direction comparator as TeamKpiOverview.js/Team360Table.js
// (Stage 4) — not Availability.js's SeasonAnalysisTab comparator, which only sorts nulls last
// when descending. Each sortable table in this codebase keeps its own copy (existing convention).
function compareStatsRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  return dir === 'asc' ? av - bv : bv - av;
}
function StatsSortHeader({ label, sortKey, activeKey, dir, onClick, align }) {
  const active = sortKey === activeKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th onClick={() => onClick(sortKey)}
      style={{ textAlign: align || 'left', padding: '8px 16px', color: active ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: 700, borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>{label} <Icon size={11} /></span>
    </th>
  );
}

export default function LineupBuilder({ user, onNavigate }) {
  const { theme } = useTheme();
  const isDay = theme === 'day';
  const accent = isDay ? '#0891b2' : '#22d3ee';

  const [period, setPeriod] = useState('week');
  const [squadRows, setSquadRows] = useState([]);
  const [loadingSquad, setLoadingSquad] = useState(true);
  const [formations, setFormations] = useState([]);
  const [formationId, setFormationId] = useState(null);

  const [slots, setSlots] = useState({});   // { slot_position: player_id }
  const [bench, setBench] = useState([]);   // [player_id, ...] ordered

  // { type: 'slot', slotPosition } | { type: 'bench-add' } | { type: 'bench-replace', index } | null
  const [pickerTarget, setPickerTarget] = useState(null);
  const [detailPlayerId, setDetailPlayerId] = useState(null);

  // Einstein's criterion dropdown (2026-07-24) — closes on outside click, same pattern as
  // PlayerPicker.js's own dropdown.
  const [einsteinMenuOpen, setEinsteinMenuOpen] = useState(false);
  const einsteinMenuRef = useRef(null);
  useEffect(() => {
    if (!einsteinMenuOpen) return;
    const onDocClick = e => { if (einsteinMenuRef.current && !einsteinMenuRef.current.contains(e.target)) setEinsteinMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [einsteinMenuOpen]);

  // Drag-and-drop (2026-07-23): { type: 'slot', slotPosition } | { type: 'bench', index } | null
  const [dragSource, setDragSource] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null); // "slot:GK" / "bench:2" / "bench:add"

  // Stage 7c: save/load/default. savedLineupId is null for a fresh, never-saved working
  // lineup — that's what decides whether the Save button creates a new lineups row or
  // updates the one it was loaded from/last saved as (confirmed requirement: never guess).
  const [savedLineupId, setSavedLineupId] = useState(null);
  const [lineupName, setLineupName] = useState('');
  const [saveModalMode, setSaveModalMode] = useState(null); // null | 'create' | 'saveAsNew'
  const [saveNameInput, setSaveNameInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [loadListOpen, setLoadListOpen] = useState(false);
  const [savedLineups, setSavedLineups] = useState([]);
  const [loadingLineups, setLoadingLineups] = useState(false);

  // Official Line-up (2026-08-07) — activeMatch non-null means the builder is currently
  // editing the official lineup for that specific match rather than a free-named working
  // lineup; see the action-button row below for how that swaps Save/Save-as-new/Load for a
  // single "Save official lineup" + "Exit" pair.
  const [officialModalOpen, setOfficialModalOpen] = useState(false);
  const [matchesForLineup, setMatchesForLineup] = useState({ played: [], scheduled: [] });
  const [loadingMatches, setLoadingMatches] = useState(false);
  // Line-up Stats (2026-08-07) — its own modal/button on the builder toolbar, next to
  // "Official Line-up", rather than a tab inside that modal (moved out so the stats table
  // isn't squeezed into the 560px match-picker width).
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [officialStats, setOfficialStats] = useState([]);
  const [loadingOfficialStats, setLoadingOfficialStats] = useState(false);
  const [statsSortKey, setStatsSortKey] = useState('starts');
  const [statsSortDir, setStatsSortDir] = useState('desc');
  const [activeMatch, setActiveMatch] = useState(null); // { date, opponentName, homeAway } | null
  const [savingOfficial, setSavingOfficial] = useState(false);

  const flashSaveMessage = (msg) => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(''), 2500);
  };

  const refreshSavedLineupsList = () => {
    getLineups().then(rows => setSavedLineups(rows || [])).catch(() => {});
  };

  // Auto-load (confirmed): opening the page with no explicit lineup requested loads whichever
  // lineup is flagged default; falls back to blank (first formation, empty slots/bench) if
  // none is flagged yet — same as Stage 7b's original behavior, not an error state. Fetched
  // alongside formations in one effect (not two) so there's no race between "formations
  // loaded" and "which formation the default lineup needs" — the default lineup's own
  // formationId is used directly rather than defaulting to formations[0] first and
  // overwriting it a moment later.
  useEffect(() => {
    Promise.all([getFormations(), getDefaultLineup().catch(() => null)]).then(([formationRows, defaultLineup]) => {
      setFormations(formationRows || []);
      if (defaultLineup) {
        setFormationId(defaultLineup.formationId);
        setSlots(defaultLineup.slots || {});
        setBench(defaultLineup.bench || []);
        setSavedLineupId(defaultLineup.id);
        setLineupName(defaultLineup.name);
      } else if (formationRows?.length) {
        setFormationId(formationRows[0].id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingSquad(true);
    getSquadPillarScores(period)
      .then(rows => setSquadRows((rows || []).map(normalizeSquadRow)))
      .catch(() => setSquadRows([]))
      .finally(() => setLoadingSquad(false));
  }, [period]);

  const scoreByPlayerId = useMemo(() => {
    const m = {};
    squadRows.forEach(r => { m[r.player_id] = r; });
    return m;
  }, [squadRows]);

  const activeFormation = formations.find(f => f.id === formationId) || null;

  // "Already assigned to ANOTHER slot or the bench" (confirmed requirement) — when the
  // picker is open to reassign a specific slot/bench index, that target's own current
  // occupant is deliberately excluded from this exclusion set, so they don't get wrongly
  // hidden from their own picker.
  const assignedPlayerIds = useMemo(() => {
    const s = new Set(bench);
    Object.values(slots).forEach(pid => { if (pid != null) s.add(pid); });
    if (pickerTarget?.type === 'slot') {
      const current = slots[pickerTarget.slotPosition];
      if (current != null) s.delete(current);
    } else if (pickerTarget?.type === 'bench-replace') {
      const current = bench[pickerTarget.index];
      if (current != null) s.delete(current);
    }
    return s;
  }, [slots, bench, pickerTarget]);

  // Confirmed formation-switch behavior (2026-07-22): where a currently-assigned player's
  // slot_position exists in the new formation too, they stay in that slot; where it doesn't
  // (e.g. back-4 -> back-3 drops one defensive slot), they move to the bench — never
  // silently dropped from the working lineup.
  const handleFormationChange = (newId) => {
    const newFormation = formations.find(f => f.id === newId);
    if (!newFormation) return;
    const keepable = new Set(newFormation.slots.map(s => s.slot_position));
    const nextSlots = {};
    const displaced = [];
    Object.entries(slots).forEach(([slotPos, playerId]) => {
      if (playerId == null) return;
      if (keepable.has(slotPos)) nextSlots[slotPos] = playerId;
      else displaced.push(playerId);
    });
    setSlots(nextSlots);
    if (displaced.length) setBench(prev => [...prev, ...displaced]);
    setFormationId(newId);
  };

  const handlePick = (playerId) => {
    if (!pickerTarget) return;
    if (pickerTarget.type === 'slot') {
      setSlots(prev => ({ ...prev, [pickerTarget.slotPosition]: playerId }));
    } else if (pickerTarget.type === 'bench-add') {
      setBench(prev => [...prev, playerId]);
    } else if (pickerTarget.type === 'bench-replace') {
      setBench(prev => prev.map((pid, i) => (i === pickerTarget.index ? playerId : pid)));
    }
    setPickerTarget(null);
  };

  const handleRemoveFromSlot = (slotPosition) => {
    setSlots(prev => {
      const next = { ...prev };
      delete next[slotPosition];
      return next;
    });
  };

  // "Clear squad" (2026-07-23) — wipes the in-memory working lineup (slots + bench) back to
  // blank. Nothing persisted until Save, but it's a one-click wipe of everything currently
  // placed, so it gets the same window.confirm guard as other destructive-feeling actions in
  // this app (e.g. TrainerRatings' delete).
  const handleClearSquad = () => {
    if (!window.confirm('Clear the entire squad — all starters and bench? This can\'t be undone (unless you reload a saved lineup).')) return;
    setSlots({});
    setBench([]);
  };

  // "Einstein" (2026-07-23, confirmed requirement; extended 2026-07-24 to rank by any single
  // pillar, not just overall) — auto-fills every currently-EMPTY starter slot with the
  // highest-`criterion` eligible player for that slot's base position, never touching slots
  // that already have someone in them. Eligible = matches position exactly (same rule
  // LineupSlotPicker's default filter uses) and isn't already a starter elsewhere; bench
  // players ARE eligible (this is "give me the best XI for the open spots", not just "use
  // players nobody's touched yet") and get pulled off the bench into their slot if picked.
  // Same null-sorts-last ranking as LineupSlotPicker's own toggleable-stat sort — a player with
  // no score yet for the chosen criterion is still better than leaving the slot empty. A
  // position with no remaining eligible candidate is left open, not forced.
  const handleEinstein = (criterion = 'overall') => {
    if (!activeFormation) return;
    const alreadyStarting = new Set(Object.values(slots).filter(v => v != null));
    let pool = squadRows.filter(r => !alreadyStarting.has(r.player_id));
    const nextSlots = { ...slots };
    const assignedThisRun = new Set();
    activeFormation.slots.forEach(slot => {
      if (nextSlots[slot.slot_position] != null) return;
      const basePos = basePositionOf(slot.slot_position);
      const eligible = pool
        .filter(r => r.position === basePos)
        .sort((a, b) => {
          const av = a[criterion], bv = b[criterion];
          const aNull = av == null, bNull = bv == null;
          if (aNull && bNull) return 0;
          if (aNull) return 1;
          if (bNull) return -1;
          return bv - av;
        });
      if (!eligible.length) return;
      const pick = eligible[0];
      nextSlots[slot.slot_position] = pick.player_id;
      assignedThisRun.add(pick.player_id);
      pool = pool.filter(r => r.player_id !== pick.player_id);
    });
    setSlots(nextSlots);
    if (assignedThisRun.size) setBench(prev => prev.filter(pid => !assignedThisRun.has(pid)));
  };

  const handleRemoveFromBench = (index) => {
    setBench(prev => prev.filter((_, i) => i !== index));
  };

  // Supports partial lineups (not all 11 slots filled) — confirmed requirement, a coach might
  // save a work-in-progress. No client-side completeness check before saving.
  const openSaveCreateModal = () => { setSaveNameInput(''); setSaveModalMode('create'); };
  const openSaveAsNewModal = () => { setSaveNameInput(lineupName ? `${lineupName} (copy)` : ''); setSaveModalMode('saveAsNew'); };

  const handleSaveSubmit = async () => {
    const name = saveNameInput.trim();
    if (!name) return;
    setSaving(true);
    try {
      const result = await createLineup({ name, formationId, slots, bench, userId: user?.id });
      setSavedLineupId(result.id);
      setLineupName(result.name);
      setSaveModalMode(null);
      flashSaveMessage('Saved as a new lineup');
      refreshSavedLineupsList();
    } catch (err) {
      flashSaveMessage(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Plain "Save" only exists once a lineup was loaded/created (savedLineupId set) — it
  // updates that same row (bumps updated_at, replaces its lineup_slots) rather than creating
  // a duplicate. Reuses the existing name with no prompt, since the name isn't changing —
  // "Save as new" (above) is the explicit, separate action for a new name/new row.
  const handleUpdateSave = async () => {
    if (!savedLineupId) return;
    setSaving(true);
    try {
      await updateLineup(savedLineupId, { name: lineupName, formationId, slots, bench });
      flashSaveMessage('Saved');
      refreshSavedLineupsList();
    } catch (err) {
      flashSaveMessage(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const openLoadList = () => {
    setLoadListOpen(true);
    setLoadingLineups(true);
    getLineups().then(rows => setSavedLineups(rows || [])).catch(() => setSavedLineups([])).finally(() => setLoadingLineups(false));
  };

  // Replaces the builder's ENTIRE in-memory state with the loaded lineup — same rendering
  // path as a fresh/blank lineup, just populated instead of empty (confirmed requirement, no
  // unsaved-changes warning for v1 — flagged back to the user as worth adding later, not
  // blocking on it here).
  const handleLoadLineup = async (id) => {
    try {
      const detail = await getLineup(id);
      setFormationId(detail.formationId);
      setSlots(detail.slots || {});
      setBench(detail.bench || []);
      setSavedLineupId(detail.id);
      setLineupName(detail.name);
      setActiveMatch(null); // loading a plain working lineup exits official-lineup mode
      setLoadListOpen(false);
    } catch (err) {
      flashSaveMessage(`Load failed: ${err.message}`);
    }
  };

  // Official Line-up (2026-08-07) — opens the match picker, fetching played + scheduled
  // matches fresh every time (a trainer might have just filled in an opponent on Week
  // Schedule, or a new match might have been GPS-uploaded since the modal was last open).
  const openOfficialModal = () => {
    setOfficialModalOpen(true);
    setLoadingMatches(true);
    getMatchesForLineup()
      .then(data => setMatchesForLineup(data || { played: [], scheduled: [] }))
      .catch(() => setMatchesForLineup({ played: [], scheduled: [] }))
      .finally(() => setLoadingMatches(false));
  };

  // Stats are fetched once per modal-open, not on every reopen — cheap enough, and a lineup
  // just saved this session wouldn't show up until reopened anyway (handleSaveOfficial clears
  // officialStats to force a refetch after a save).
  const openStatsModal = () => {
    setStatsModalOpen(true);
    if (!officialStats.length) {
      setLoadingOfficialStats(true);
      getOfficialLineupStats().then(rows => setOfficialStats(rows || [])).catch(() => setOfficialStats([])).finally(() => setLoadingOfficialStats(false));
    }
  };
  const toggleStatsSort = key => {
    if (statsSortKey === key) setStatsSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setStatsSortKey(key); setStatsSortDir(key === 'name' ? 'asc' : 'desc'); }
  };
  const sortedOfficialStats = useMemo(
    () => [...officialStats].sort((a, b) => compareStatsRows(a, b, statsSortKey, statsSortDir)),
    [officialStats, statsSortKey, statsSortDir]
  );

  // Picking a match either opens its existing official lineup (same "replace the builder's
  // entire in-memory state" shape as handleLoadLineup above) or starts a blank one tagged to
  // that match — formationId is deliberately left untouched when starting blank, same as a
  // fresh page load, rather than forced back to formations[0].
  const handleSelectMatch = async (match) => {
    try {
      const existing = await getOfficialLineup(match.date, match.opponentName);
      if (existing) {
        setFormationId(existing.formationId);
        setSlots(existing.slots || {});
        setBench(existing.bench || []);
        setLineupName(existing.name);
      } else {
        setSlots({});
        setBench([]);
        setLineupName(`vs ${match.opponentName} (${match.date})`);
      }
      setSavedLineupId(null);
      setActiveMatch({ date: match.date, opponentName: match.opponentName, homeAway: match.homeAway, logoUrl: match.logoUrl || null });
      setOfficialModalOpen(false);
    } catch (err) {
      flashSaveMessage(`Load failed: ${err.message}`);
    }
  };

  const handleExitOfficialMode = () => setActiveMatch(null);

  // Upserts keyed on (matchDate, opponentName) server-side — no separate create/update branch
  // needed here the way the plain Save/Save-as-new flow above needs one, since the backend
  // already knows whether that match has an official lineup yet.
  const handleSaveOfficial = async () => {
    if (!activeMatch) return;
    setSavingOfficial(true);
    try {
      const result = await saveOfficialLineup({
        matchDate: activeMatch.date, opponentName: activeMatch.opponentName, homeAway: activeMatch.homeAway,
        name: lineupName, formationId, slots, bench, userId: user?.id,
      });
      setLineupName(result.name);
      flashSaveMessage('Official lineup saved');
      setOfficialStats([]);
    } catch (err) {
      flashSaveMessage(`Save failed: ${err.message}`);
    } finally {
      setSavingOfficial(false);
    }
  };

  // Atomic unset-old/set-new happens server-side in one transaction (Stage 7a's partial
  // unique index would otherwise reject a naive "just set this one true" while another row
  // is still true) — this just triggers it and refreshes the list to reflect the new state.
  const handleSetDefault = async (id, e) => {
    e.stopPropagation();
    try {
      await setDefaultLineup(id);
      refreshSavedLineupsList();
    } catch (err) {
      flashSaveMessage(`Set default failed: ${err.message}`);
    }
  };

  // Drag-and-drop rearranging (2026-07-23) — native HTML5 DnD, no library. dragSource is the
  // location a drag started from; dragOverKey is a plain string ("slot:GK" / "bench:2" /
  // "bench:add") identifying whichever drop target is currently highlighted. Every case
  // (slot<->slot, slot<->bench, bench<->bench) is a straight SWAP of the two locations'
  // occupants — not a shift/reorder — simplest semantics to reason about and matches what a
  // coach expects when dragging one card onto another. Dropping onto the bench's empty
  // "+Add" button is the one non-swap case: the dragged player just moves there (appended).
  const locKey = (loc) => (loc.type === 'slot' ? `slot:${loc.slotPosition}` : `bench:${loc.index}`);
  const getPlayerAtLoc = (loc) => (loc.type === 'slot' ? (slots[loc.slotPosition] ?? null) : (bench[loc.index] ?? null));

  const handleDragStart = (loc) => (e) => {
    setDragSource(loc);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires setData to be called for the drag to actually start.
    e.dataTransfer.setData('text/plain', locKey(loc));
  };

  const handleDropOnLoc = (target) => (e) => {
    e.preventDefault();
    setDragOverKey(null);
    const source = dragSource;
    setDragSource(null);
    if (!source || locKey(source) === locKey(target)) return;
    const sourcePlayer = getPlayerAtLoc(source);
    if (sourcePlayer == null) return;
    const targetPlayer = getPlayerAtLoc(target);
    swapLocations(source, target, sourcePlayer, targetPlayer);
  };

  const handleDropOnBenchAdd = (e) => {
    e.preventDefault();
    setDragOverKey(null);
    const source = dragSource;
    setDragSource(null);
    if (!source) return;
    const sourcePlayer = getPlayerAtLoc(source);
    if (sourcePlayer == null) return;
    if (source.type === 'slot') setSlots(prev => { const next = { ...prev }; delete next[source.slotPosition]; return next; });
    else setBench(prev => prev.filter((_, i) => i !== source.index));
    setBench(prev => [...prev, sourcePlayer]);
  };

  function swapLocations(source, target, sourcePlayer, targetPlayer) {
    if (source.type === 'slot' && target.type === 'slot') {
      setSlots(prev => {
        const next = { ...prev };
        next[target.slotPosition] = sourcePlayer;
        if (targetPlayer == null) delete next[source.slotPosition]; else next[source.slotPosition] = targetPlayer;
        return next;
      });
    } else if (source.type === 'bench' && target.type === 'bench') {
      // Both bench indices are guaranteed occupied here — an empty bench position only
      // exists as the trailing "+Add" button, which goes through handleDropOnBenchAdd
      // instead, never through this swap path.
      setBench(prev => {
        const next = [...prev];
        next[source.index] = targetPlayer;
        next[target.index] = sourcePlayer;
        return next;
      });
    } else {
      // One slot, one bench — mixed swap touches both structures together.
      const slotLoc = source.type === 'slot' ? source : target;
      const benchLoc = source.type === 'bench' ? source : target;
      const slotPlayer = source.type === 'slot' ? sourcePlayer : targetPlayer;
      const benchPlayer = source.type === 'bench' ? sourcePlayer : targetPlayer;
      setSlots(prev => {
        const next = { ...prev };
        if (benchPlayer == null) delete next[slotLoc.slotPosition]; else next[slotLoc.slotPosition] = benchPlayer;
        return next;
      });
      setBench(prev => {
        const next = [...prev];
        if (slotPlayer == null) next.splice(benchLoc.index, 1); else next[benchLoc.index] = slotPlayer;
        return next;
      });
    }
  }

  // Lineup overall = average of the starters' overall at the selected period, excluding any
  // starter whose overall is NULL for that period (never treated as 0) — same principle as
  // every other "no data ≠ 0" average in this build. `scoredCount`/`totalStarters` surface
  // when the figure is based on fewer than a full XI.
  const { lineupOverall, lineupStars, scoredCount, totalStarters } = useMemo(() => {
    if (!activeFormation) return { lineupOverall: null, lineupStars: null, scoredCount: 0, totalStarters: 0 };
    const starterIds = activeFormation.slots.map(s => slots[s.slot_position]).filter(v => v != null);
    const overalls = starterIds.map(pid => scoreByPlayerId[pid]?.overall).filter(v => v != null);
    const overall = overalls.length ? overalls.reduce((a, b) => a + b, 0) / overalls.length : null;
    const stars = overall == null ? null : Math.round((overall / 100 * 5) * 2) / 2;
    return { lineupOverall: overall, lineupStars: stars, scoredCount: overalls.length, totalStarters: activeFormation.slots.length };
  }, [activeFormation, slots, scoreByPlayerId]);

  // 5-pillar KPI bars (2026-07-23) — same "average across players, excluding NULL rather
  // than treating it as 0" pattern as Home's squad-average tile, but scoped to the currently
  // selected squad (this lineup's 11 starters), not the whole active roster.
  const starterPillarAverages = useMemo(() => {
    const out = {};
    if (!activeFormation) { PILLAR_KEYS.forEach(k => { out[k] = null; }); return out; }
    const starterIds = activeFormation.slots.map(s => slots[s.slot_position]).filter(v => v != null);
    PILLAR_KEYS.forEach(k => {
      const vals = starterIds.map(pid => scoreByPlayerId[pid]?.[k]).filter(v => v != null);
      out[k] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    });
    return out;
  }, [activeFormation, slots, scoreByPlayerId]);

  const pickerBasePosition = pickerTarget?.type === 'slot' ? basePositionOf(pickerTarget.slotPosition) : null;
  const pickerTitle = pickerTarget?.type === 'slot'
    ? `Pick ${basePositionOf(pickerTarget.slotPosition)}`
    : pickerTarget?.type === 'bench-replace' ? 'Change bench player' : 'Add to bench';

  const detailPlayerRow = detailPlayerId != null ? scoreByPlayerId[detailPlayerId] : null;
  const detailPlayer = detailPlayerRow ? {
    id: detailPlayerRow.player_id, name: detailPlayerRow.name,
    position: detailPlayerRow.position, photo_url: detailPlayerRow.photo_url,
  } : null;

  return (
    <PageChrome onNavigate={onNavigate} user={user}>
      {/* Full width, not a centered maxWidth column (2026-07-23) — the sidebar pins to the
          true left edge of the page and the pitch centers in the true full width below;
          a constrained column would just recreate the "empty gap on the left" complaint at
          a different width. */}
      <div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1cm' }}>
          <div style={{ display: 'inline-flex', gap: 6, background: isDay ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.06)', border: `1px solid ${isDay ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)'}`, padding: 5, borderRadius: 999, backdropFilter: 'blur(10px)' }}>
            {PERIODS.map(p => {
              const active = period === p.value;
              return (
                <button key={p.value} onClick={() => setPeriod(p.value)}
                  style={{
                    padding: '8px 18px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                    color: active ? '#04141a' : (isDay ? 'rgba(20,24,28,0.55)' : 'rgba(255,255,255,0.65)'),
                    background: active ? 'linear-gradient(135deg,#22d3ee,#0891b2)' : 'transparent',
                  }}>
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
          <div style={{ display: 'inline-flex', gap: 6, background: isDay ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.06)', border: `1px solid ${isDay ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)'}`, padding: 5, borderRadius: 999, backdropFilter: 'blur(10px)' }}>
            {formations.map(f => {
              const active = formationId === f.id;
              return (
                <button key={f.id} onClick={() => handleFormationChange(f.id)}
                  style={{
                    padding: '9px 22px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer', border: 'none',
                    color: active ? '#04141a' : (isDay ? 'rgba(20,24,28,0.55)' : 'rgba(255,255,255,0.65)'),
                    background: active ? 'linear-gradient(135deg,#22d3ee,#0891b2)' : 'transparent',
                  }}>
                  {f.name}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          {activeMatch ? (
            <>
              {activeMatch.logoUrl && (
                <img src={activeMatch.logoUrl} alt="" style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }} />
              )}
              <span style={{ fontSize: 12, color: isDay ? 'rgba(20,24,28,0.5)' : 'rgba(255,255,255,0.5)' }}>
                Official line-up: <strong style={{ color: isDay ? '#1c2024' : '#fff' }}>vs {activeMatch.opponentName} ({formatMatchDate(activeMatch.date)})</strong>
              </span>
              <ActionButton onClick={handleSaveOfficial} icon={Save} isDay={isDay} disabled={savingOfficial}>
                {savingOfficial ? 'Saving…' : `Save to ${activeMatch.opponentName}`}
              </ActionButton>
              <ActionButton onClick={handleExitOfficialMode} icon={X} isDay={isDay}>Exit</ActionButton>
            </>
          ) : (
            <>
              {savedLineupId && (
                <span style={{ fontSize: 12, color: isDay ? 'rgba(20,24,28,0.5)' : 'rgba(255,255,255,0.5)' }}>
                  Editing: <strong style={{ color: isDay ? '#1c2024' : '#fff' }}>{lineupName}</strong>
                </span>
              )}
              {!savedLineupId ? (
                <ActionButton onClick={openSaveCreateModal} icon={Save} isDay={isDay}>Save lineup</ActionButton>
              ) : (
                <>
                  <ActionButton onClick={handleUpdateSave} icon={Save} isDay={isDay} disabled={saving}>Save</ActionButton>
                  <ActionButton onClick={openSaveAsNewModal} icon={Copy} isDay={isDay}>Save as new</ActionButton>
                </>
              )}
            </>
          )}
          <ActionButton onClick={openLoadList} icon={FolderOpen} isDay={isDay}>Load lineup</ActionButton>
          <ActionButton onClick={openOfficialModal} icon={Calendar} isDay={isDay}>Official Line-up</ActionButton>
          <ActionButton onClick={openStatsModal} icon={BarChart3} isDay={isDay}>Line-up Stats</ActionButton>
          {saveMessage && <span style={{ fontSize: 12, color: accent, fontWeight: 700 }}>{saveMessage}</span>}
        </div>

        {loadingSquad ? (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 30 }}>Loading squad…</p>
        ) : !activeFormation ? (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 30 }}>Loading formation…</p>
        ) : (
          <>
            {/* Full-bleed pitch section (2026-07-23 redesign — real photographic-perspective
                pitch image instead of a flat CSS rectangle). The outer container is sized to
                the real viewport (100vw via the standard "break out of a constrained parent"
                trick — 100vw width + a negative left margin pulling it back to the true
                viewport edge — and 100vh tall), not a small fixed-pixel canvas shrunk to fit a
                narrower column. The trapezoid pitch panel is a CHILD sized relative to that
                container (aspect-ratio locked to the source image, fit via min()) — never "the
                whole page shrunk into a corner" the way the old fixed 680x820 canvas could be
                on a wide viewport. Sidebar stays position:absolute, pinned to the container's
                own top-left; the centering layer reserves that width via paddingLeft so the
                pitch centers in the space actually left over, not literally the full width
                (which would put it half-behind the sidebar). */}
            <div style={{
              position: 'relative', width: '100vw', height: '100vh', marginLeft: 'calc(50% - 50vw)', marginTop: 20,
              background: isDay
                ? 'linear-gradient(180deg, #eef1f3, #e3e7ea 55%, #d7dce0)'
                : 'linear-gradient(180deg, #0c0e11, #08090b 55%, #050607)',
            }}>
              <div style={{ position: 'absolute', left: 32, top: 32, width: 260, zIndex: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
                    background: isDay
                      ? 'radial-gradient(circle at 50% 40%, rgba(34,211,238,0.18), rgba(255,255,255,0.9) 70%)'
                      : 'radial-gradient(circle at 50% 40%, rgba(34,211,238,0.20), rgba(20,22,26,0.6) 70%)',
                    border: `2px solid ${isDay ? 'rgba(8,145,178,0.4)' : 'rgba(34,211,238,0.4)'}`,
                    boxShadow: `0 0 26px ${isDay ? 'rgba(34,211,238,0.15)' : 'rgba(34,211,238,0.2)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <img src={`${process.env.PUBLIC_URL}/heracles-crest.png`} alt="Heracles Almelo" style={{ width: 36, height: 'auto' }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "'Archivo Black', 'Arial Black', Impact, sans-serif", fontSize: 19, color: isDay ? '#1c2024' : '#fff', letterSpacing: '0.4px' }}>
                      Heracles Almelo
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <span style={{ fontSize: 17, fontWeight: 800, color: accent }}>
                        {lineupOverall == null ? '—' : Math.round(lineupOverall)}
                      </span>
                      {lineupStars != null && (
                        <StarRating value={lineupStars} size={13} color={accent} emptyColor={isDay ? 'rgba(20,24,28,0.15)' : 'rgba(255,255,255,0.2)'} showNumber={false} gap={2} />
                      )}
                    </div>
                  </div>
                </div>
                {lineupStars == null && (
                  <div style={{ fontSize: 12, color: isDay ? 'rgba(20,24,28,0.4)' : 'rgba(255,255,255,0.4)', marginTop: 6 }}>No rated starters yet</div>
                )}
                {totalStarters > 0 && scoredCount < totalStarters && scoredCount > 0 && (
                  <div style={{ fontSize: 11, color: isDay ? 'rgba(20,24,28,0.4)' : 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                    Based on {scoredCount} of {totalStarters} scored starters
                  </div>
                )}

                <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {PILLAR_KEYS.map(k => (
                    <KpiBar key={k} label={PILLAR_LABELS[k]} value={starterPillarAverages[k]} isDay={isDay} accent={accent} />
                  ))}
                </div>

                <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <SidebarActionButton onClick={handleClearSquad} icon={Trash2} isDay={isDay} tone="danger">
                    Clear squad
                  </SidebarActionButton>
                  {/* Einstein now ranks by any single pillar, not just overall (2026-07-24) —
                      the button opens a small dropdown of criteria instead of running overall
                      unconditionally; picking one fills every open slot by that stat and
                      closes the menu. */}
                  <div ref={einsteinMenuRef} style={{ position: 'relative' }}>
                    <SidebarActionButton onClick={() => setEinsteinMenuOpen(o => !o)} icon={Sparkles} isDay={isDay}>
                      Einstein
                    </SidebarActionButton>
                    {einsteinMenuOpen && (
                      <div style={{
                        position: 'absolute', top: '110%', left: 0, right: 0, zIndex: 10, marginTop: 4,
                        background: isDay ? '#fff' : '#15181c', border: `1px solid ${isDay ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)'}`,
                        borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.35)', overflow: 'hidden',
                      }}>
                        {EINSTEIN_CRITERIA.map(c => (
                          <button key={c.key} type="button"
                            onClick={() => { handleEinstein(c.key); setEinsteinMenuOpen(false); }}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', background: 'transparent',
                              color: isDay ? '#1c2024' : '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = isDay ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* True viewport-center, not offset for the sidebar (2026-07-23) — the trapezoid
                  is narrowest right at the top, where the sidebar (crest/score/KPI bars) sits,
                  so a true center leaves that top corner's grass well clear of the sidebar's
                  ~292px-wide column without needing to reserve space and skew the whole pitch
                  right. */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  position: 'relative',
                  width: `min(100%, ${PITCH_IMAGE_ASPECT * 100}vh)`,
                  aspectRatio: `${PITCH_IMAGE_ASPECT}`,
                  maxHeight: '100%',
                }}>
                  {/* Clipped to the trapezoid's own measured corners (PITCH_CLIP_PATH) so the
                      image's transparent margin never shows — the surrounding dark/light
                      surface is this container's own background above, not baked into the PNG. */}
                  <img
                    src={`${process.env.PUBLIC_URL}/pitch-3d.png`} alt=""
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', clipPath: PITCH_CLIP_PATH }}
                  />

                  {activeFormation.slots.map(slot => {
                    const playerId = slots[slot.slot_position];
                    const row = playerId != null ? scoreByPlayerId[playerId] : null;
                    const loc = { type: 'slot', slotPosition: slot.slot_position };
                    const key = locKey(loc);
                    // Existing formation data stores x/y as 0-100 on the old flat canvas —
                    // reused unchanged as lateral/u fractions into the trapezoid formula.
                    const x = pitchSlotX(slot.x / 100, slot.y / 100);
                    const y = pitchSlotY(slot.y / 100);
                    return (
                      <div key={slot.slot_position} style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}>
                        {row ? (
                          <PlayerCard
                            row={row}
                            onView={() => setDetailPlayerId(playerId)}
                            onReassign={() => setPickerTarget({ type: 'slot', slotPosition: slot.slot_position })}
                            onRemove={() => handleRemoveFromSlot(slot.slot_position)}
                            onDragStart={handleDragStart(loc)}
                            onDragOver={e => e.preventDefault()}
                            onDrop={handleDropOnLoc(loc)}
                            onDragEnter={() => setDragOverKey(key)}
                            onDragLeave={() => setDragOverKey(k => (k === key ? null : k))}
                            isDragOver={dragOverKey === key}
                          />
                        ) : (
                          <EmptySlotCard
                            label={basePositionOf(slot.slot_position)} isDay={isDay}
                            onClick={() => setPickerTarget({ type: 'slot', slotPosition: slot.slot_position })}
                            onDragOver={e => e.preventDefault()}
                            onDrop={handleDropOnLoc(loc)}
                            onDragEnter={() => setDragOverKey(key)}
                            onDragLeave={() => setDragOverKey(k => (k === key ? null : k))}
                            isDragOver={dragOverKey === key}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Bench + tier legend get their own centered column (matching the pitch's own
                width) rather than spanning the full page — only the sidebar+pitch row above
                needed the full-width treatment. */}
            <div style={{ maxWidth: 760, margin: '0 auto' }}>
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: isDay ? 'rgba(20,24,28,0.4)' : 'rgba(255,255,255,0.4)', marginBottom: 12, paddingLeft: 10 }}>
                  Bench
                </div>
                {/* padding on every side, not just bottom (2026-07-23) — the remove/reassign
                    overlay icons sit at a -6px offset outside each card's own box, and
                    overflowX:'auto' forces the container to also clip vertically per the CSS
                    spec (an axis can't stay 'visible' once the other is anything but), so
                    those icons were getting cut off top/left/right with no padding to absorb
                    the -6px reach. */}
                <div style={{ display: 'flex', gap: 18, overflowX: 'auto', padding: '10px 10px 8px', flexWrap: 'wrap' }}>
                  {bench.map((playerId, i) => {
                    const row = scoreByPlayerId[playerId];
                    if (!row) return null;
                    const loc = { type: 'bench', index: i };
                    const key = locKey(loc);
                    return (
                      <PlayerCard
                        key={`${playerId}-${i}`} row={row} compact
                        onView={() => setDetailPlayerId(playerId)}
                        onReassign={() => setPickerTarget({ type: 'bench-replace', index: i })}
                        onRemove={() => handleRemoveFromBench(i)}
                        onDragStart={handleDragStart(loc)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={handleDropOnLoc(loc)}
                        onDragEnter={() => setDragOverKey(key)}
                        onDragLeave={() => setDragOverKey(k => (k === key ? null : k))}
                        isDragOver={dragOverKey === key}
                      />
                    );
                  })}
                  <EmptySlotCard label="Add" isDay={isDay} compact onClick={() => setPickerTarget({ type: 'bench-add' })}
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleDropOnBenchAdd}
                    onDragEnter={() => setDragOverKey('bench:add')}
                    onDragLeave={() => setDragOverKey(k => (k === 'bench:add' ? null : k))}
                    isDragOver={dragOverKey === 'bench:add'}
                  />
                </div>
              </div>

              {/* Tier legend */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 26, flexWrap: 'wrap', marginTop: 34 }}>
                {[...TIERS].reverse().map(t => (
                  <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: t.bg, border: t.border ? `1px solid ${t.border}` : 'none' }} />
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDay ? 'rgba(20,24,28,0.55)' : 'rgba(255,255,255,0.55)' }}>{t.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <LineupSlotPicker
        open={!!pickerTarget}
        squadRows={squadRows}
        assignedPlayerIds={assignedPlayerIds}
        basePosition={pickerBasePosition}
        title={pickerTitle}
        onPick={handlePick}
        onClose={() => setPickerTarget(null)}
      />

      {detailPlayer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '32px 16px' }}
          onClick={() => setDetailPlayerId(null)}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setDetailPlayerId(null)}
              style={{ position: 'absolute', top: -6, right: -6, width: 32, height: 32, borderRadius: '50%', border: 'none', background: isDay ? '#fff' : '#1e293b', color: isDay ? '#1c2024' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, boxShadow: '0 4px 14px rgba(0,0,0,0.3)' }}>
              <X size={16} />
            </button>
            <PlayerDetailPanel player={detailPlayer} period={period} onPeriodChange={setPeriod} />
          </div>
        </div>
      )}

      {saveModalMode && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setSaveModalMode(null)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 14, padding: 20, width: 360, maxWidth: '100%', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: 'var(--text-primary)' }}>
              {saveModalMode === 'create' ? 'Save lineup' : 'Save as new lineup'}
            </div>
            <input autoFocus value={saveNameInput} onChange={e => setSaveNameInput(e.target.value)}
              placeholder="e.g. vs Ajax away, high press"
              onKeyDown={e => { if (e.key === 'Enter') handleSaveSubmit(); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setSaveModalMode(null)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={handleSaveSubmit} disabled={!saveNameInput.trim() || saving}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: !saveNameInput.trim() || saving ? 'var(--border-strong)' : accent, color: isDay ? '#04141a' : '#04141a', cursor: !saveNameInput.trim() || saving ? 'default' : 'pointer', fontSize: 13, fontWeight: 700 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loadListOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setLoadListOpen(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 14, width: 480, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Saved lineups</div>
              <button onClick={() => setLoadListOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingLineups && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}
              {!loadingLineups && !savedLineups.length && (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-dimmed)' }}>No saved lineups yet.</div>
              )}
              {!loadingLineups && savedLineups.map(l => (
                <button key={l.id} type="button" onClick={() => handleLoadLineup(l.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', border: 'none', borderTop: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-row-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {l.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {l.formationName} · {l.starterCount}/11 starters · {l.benchCount} bench · {new Date(l.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span onClick={e => handleSetDefault(l.id, e)} title={l.isDefault ? 'Default lineup' : 'Set as default'}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', padding: 4, color: l.isDefault ? '#f59e0b' : 'var(--text-dimmed)', flexShrink: 0 }}>
                    <StarIcon size={16} fill={l.isDefault ? '#f59e0b' : 'none'} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {officialModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setOfficialModalOpen(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                <Calendar size={14} /> Official Line-up
              </span>
              <button onClick={() => setOfficialModalOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingMatches && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}
              {!loadingMatches && !matchesForLineup.scheduled.length && !matchesForLineup.played.length && (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-dimmed)' }}>
                  No matches found. Fill in an opponent on Week Schedule, or play a match with GPS data logged.
                </div>
              )}
              {!loadingMatches && matchesForLineup.scheduled.length > 0 && (
                <>
                  <div style={{ padding: '10px 16px 4px', fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Upcoming</div>
                  {matchesForLineup.scheduled.map((m, i) => (
                    <MatchRow key={`sch-${i}`} match={m} onClick={() => handleSelectMatch(m)} />
                  ))}
                </>
              )}
              {!loadingMatches && matchesForLineup.played.length > 0 && (
                <>
                  <div style={{ padding: '10px 16px 4px', fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Played</div>
                  {matchesForLineup.played.map((m, i) => (
                    <MatchRow key={`pl-${i}`} match={m} onClick={() => handleSelectMatch(m)} />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {statsModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setStatsModalOpen(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 14, width: 860, maxWidth: '100%', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                <BarChart3 size={14} /> Line-up Stats
              </span>
              <button onClick={() => setStatsModalOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingOfficialStats && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}
              {!loadingOfficialStats && !officialStats.length && (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-dimmed)' }}>No official lineups saved yet.</div>
              )}
              {!loadingOfficialStats && officialStats.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <StatsSortHeader label="Player" sortKey="name" activeKey={statsSortKey} dir={statsSortDir} onClick={toggleStatsSort} />
                      <StatsSortHeader label="Starts" sortKey="starts" activeKey={statsSortKey} dir={statsSortDir} onClick={toggleStatsSort} align="right" />
                      <StatsSortHeader label="Squad" sortKey="squadAppearances" activeKey={statsSortKey} dir={statsSortDir} onClick={toggleStatsSort} align="right" />
                      <StatsSortHeader label="Total mins" sortKey="totalMins" activeKey={statsSortKey} dir={statsSortDir} onClick={toggleStatsSort} align="right" />
                      <StatsSortHeader label="Matches" sortKey="matchesPlayed" activeKey={statsSortKey} dir={statsSortDir} onClick={toggleStatsSort} align="right" />
                      <StatsSortHeader label="Avg mins" sortKey="avgMinsPerGame" activeKey={statsSortKey} dir={statsSortDir} onClick={toggleStatsSort} align="right" />
                      <StatsSortHeader label="Subbed in" sortKey="subbedIn" activeKey={statsSortKey} dir={statsSortDir} onClick={toggleStatsSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOfficialStats.map(r => (
                      <tr key={r.playerId}>
                        <td style={{ padding: '7px 16px', color: 'var(--text-primary)', borderTop: '1px solid var(--border)' }}>{r.name}</td>
                        <td style={{ padding: '7px 16px', textAlign: 'right', color: 'var(--text-primary)', fontWeight: 700, borderTop: '1px solid var(--border)' }}>{r.starts}</td>
                        <td style={{ padding: '7px 16px', textAlign: 'right', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}>{r.squadAppearances}</td>
                        <td style={{ padding: '7px 16px', textAlign: 'right', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}>{r.totalMins}</td>
                        <td style={{ padding: '7px 16px', textAlign: 'right', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}>{r.matchesPlayed}</td>
                        <td style={{ padding: '7px 16px', textAlign: 'right', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}>{r.avgMinsPerGame ?? '—'}</td>
                        <td style={{ padding: '7px 16px', textAlign: 'right', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}>{r.subbedIn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </PageChrome>
  );
}
